// Circle picture (Year 7, 1 Nov 2026). A `circle_parts` is a circle with a
// point at its centre, and any of: a line from the centre to the circle, a
// line right across it through the centre, and a label on its curved edge.
// The points may carry letters ("M", "T"), and the lines and the edge a
// length ("4 m"), a letter, "?" or the name of the part.
//
// The name of a part ("radius", "diameter", "circumference") is shown only
// when the question is not asking which part is which; the screen reader
// never names a part that is not written on the picture (wording approved
// by the owner, 1 Nov 2026).
//
// Pure parsing and geometry only, no components (see
// practice-diagram-data.ts).

export type CircleLine = {
  // The letter at the end of the line on the circle, or "".
  ends: string[];
  label: string;
};

export type CirclePartsSpec = {
  type: 'circle_parts';
  // The letter at the centre, the word "centre", or "".
  centre: string;
  // From the centre to the circle.
  radius: CircleLine | null;
  // Across the circle through the centre.
  diameter: CircleLine | null;
  // On the curved edge.
  edge: string;
};

const LENGTH = /^\d+(?:\.\d+)?(?: ?(?:mm|cm|m|km))?$/;
const LETTER = /^[A-Z]$/;
const LINE_LETTER = /^[a-z]$/;

// What may be written on each part: a length, a small letter such as r,
// "?", "" or the part's own name.
function readPartLabel(value: unknown, name: string): string | null {
  if (value === undefined) return '';
  if (typeof value !== 'string') return null;
  const text = value.trim();
  if (
    text === '' ||
    text === '?' ||
    text === name ||
    LENGTH.test(text) ||
    LINE_LETTER.test(text)
  )
    return text;
  return null;
}

function readLine(
  value: unknown,
  name: 'radius' | 'diameter',
): CircleLine | null | undefined {
  if (value === undefined) return null;
  if (typeof value !== 'object' || value === null || Array.isArray(value))
    return undefined;
  const record = value as Record<string, unknown>;
  const count = name === 'radius' ? 1 : 2;
  const given =
    name === 'radius'
      ? record.end === undefined
        ? []
        : [record.end]
      : Array.isArray(record.ends)
        ? (record.ends as unknown[])
        : record.ends === undefined
          ? []
          : null;
  if (given === null || (given.length !== 0 && given.length !== count))
    return undefined;
  const ends: string[] = [];
  for (const end of given) {
    if (typeof end !== 'string') return undefined;
    const text = end.trim();
    if (text !== '' && !LETTER.test(text)) return undefined;
    ends.push(text);
  }
  const label = readPartLabel(record.label, name);
  if (label === null) return undefined;
  return { ends, label };
}

export function readCircleParts(
  record: Record<string, unknown>,
): CirclePartsSpec | null {
  const centre = record.centre === undefined ? '' : record.centre;
  if (
    typeof centre !== 'string' ||
    !(centre === '' || centre === 'centre' || LETTER.test(centre))
  )
    return null;
  const radius = readLine(record.radius, 'radius');
  const diameter = readLine(record.diameter, 'diameter');
  if (radius === undefined || diameter === undefined) return null;
  const edge = readPartLabel(record.edge, 'circumference');
  if (edge === null) return null;
  // No two points share a letter.
  const letters = [
    LETTER.test(centre) ? centre : '',
    ...(radius?.ends ?? []),
    ...(diameter?.ends ?? []),
  ].filter((letter) => letter !== '');
  if (new Set(letters).size !== letters.length) return null;
  return { type: 'circle_parts', centre, radius, diameter, edge };
}

function spokenPart(text: string): string {
  if (text === '?') return 'is unknown and is marked with a question mark';
  if (LINE_LETTER.test(text)) return `is labelled the letter ${text}`;
  return `is labelled ${text}`;
}

export function describeCircleParts(circle: CirclePartsSpec): string {
  const letter = LETTER.test(circle.centre) ? circle.centre : '';
  const centre = letter === '' ? 'the centre' : letter;
  const parts = [
    letter === ''
      ? 'A circle with a point marked at its centre.'
      : `A circle with a point ${letter} marked at its centre.`,
  ];
  if (circle.centre === 'centre') parts.push('The point is labelled centre.');
  const { radius, diameter } = circle;
  if (radius) {
    const end = radius.ends[0] ?? '';
    parts.push(
      `A straight line goes from ${centre} to ${end === '' ? 'a point' : `a point ${end}`} on the circle.`,
    );
  }
  if (diameter) {
    const [from = '', to = ''] = diameter.ends;
    parts.push(
      from === ''
        ? `A straight line goes across the circle through ${centre}.`
        : `A straight line goes across the circle through ${centre}, from point ${from} to point ${to}.`,
    );
  }
  if (radius && radius.label !== '')
    parts.push(
      `The line from ${centre} to the circle ${spokenPart(radius.label)}.`,
    );
  if (diameter && diameter.label !== '')
    parts.push(`The line across the circle ${spokenPart(diameter.label)}.`);
  if (circle.edge !== '')
    parts.push(`The curved edge of the circle ${spokenPart(circle.edge)}.`);
  return parts.join(' ');
}

// ---------------------------------------------------------------------------
// Where everything is drawn, in units close to one pixel each at the
// picture's natural width. The centre is at 0, 0.

export type CirclePoint = { x: number; y: number };
export type CircleText = CirclePoint & { text: string; part: string };

export type CirclePicture = {
  radius: number;
  lines: { from: CirclePoint; to: CirclePoint; part: string }[];
  // The centre and every lettered point on the circle.
  dots: CirclePoint[];
  texts: CircleText[];
  box: { x: number; y: number; width: number; height: number };
};

const RADIUS = 72;
// The line from the centre leaves up and to the right.
const RADIUS_BEARING = 45;
export const CIRCLE_LABEL_SIZE = 14;
const LETTER_WIDTH = 8.4;
const TEXT_HEIGHT = 15;
const GAP = 12;
const PAD = 6;

function toward(bearing: number): CirclePoint {
  const turn = (bearing * Math.PI) / 180;
  return { x: Math.sin(turn), y: -Math.cos(turn) };
}

function widthOf(text: string): number {
  return text.length * LETTER_WIDTH;
}

export function circlePartsPicture(circle: CirclePartsSpec): CirclePicture {
  const centre = { x: 0, y: 0 };
  const lines: CirclePicture['lines'] = [];
  const dots: CirclePoint[] = [centre];
  const texts: CircleText[] = [];
  const out = (way: CirclePoint, text: string) => ({
    x: way.x * (RADIUS + GAP + widthOf(text) / 2),
    y: way.y * (RADIUS + GAP + TEXT_HEIGHT / 2),
  });
  if (circle.radius) {
    const way = toward(RADIUS_BEARING);
    const end = { x: way.x * RADIUS, y: way.y * RADIUS };
    lines.push({ from: centre, to: end, part: 'radius' });
    const [letter = ''] = circle.radius.ends;
    if (letter !== '') {
      dots.push(end);
      texts.push({ ...out(way, letter), text: letter, part: 'point' });
    }
    const { label } = circle.radius;
    if (label !== '') {
      // Above the line, to its left.
      const side = toward(RADIUS_BEARING - 90);
      const reach = GAP + (Math.abs(side.x) * widthOf(label)) / 2;
      texts.push({
        x: end.x / 2 + side.x * reach,
        y: end.y / 2 + side.y * (GAP + TEXT_HEIGHT / 2),
        text: label,
        part: 'radius',
      });
    }
  }
  if (circle.diameter) {
    const left = { x: -RADIUS, y: 0 };
    const right = { x: RADIUS, y: 0 };
    lines.push({ from: left, to: right, part: 'diameter' });
    const [from = '', to = ''] = circle.diameter.ends;
    if (from !== '') {
      dots.push(left, right);
      texts.push({ ...out({ x: -1, y: 0 }, from), text: from, part: 'point' });
      texts.push({ ...out({ x: 1, y: 0 }, to), text: to, part: 'point' });
    }
    const { label } = circle.diameter;
    // Below the line, on the left half, clear of the centre's letter.
    if (label !== '')
      texts.push({
        x: -RADIUS / 2,
        y: GAP + TEXT_HEIGHT / 2 - 2,
        text: label,
        part: 'diameter',
      });
  }
  if (circle.centre !== '') {
    // Below the centre, clear of the line from it, which leaves up and to
    // the right. When a label sits below the line across on the left, the
    // centre's label moves to the right of it.
    const crowded = circle.diameter !== null && circle.diameter.label !== '';
    const width = widthOf(circle.centre);
    texts.push({
      x: crowded ? 4 + width / 2 : circle.centre === 'centre' ? 0 : -GAP,
      y: GAP + TEXT_HEIGHT / 2 - 2,
      text: circle.centre,
      part: 'centre',
    });
  }
  // Under the circle, outside it.
  if (circle.edge !== '')
    texts.push({
      x: 0,
      y: RADIUS + GAP + TEXT_HEIGHT / 2 - 4,
      text: circle.edge,
      part: 'edge',
    });
  const xs = [-RADIUS, RADIUS];
  const ys = [-RADIUS, RADIUS];
  for (const text of texts) {
    xs.push(text.x - widthOf(text.text) / 2, text.x + widthOf(text.text) / 2);
    ys.push(text.y - TEXT_HEIGHT / 2, text.y + TEXT_HEIGHT / 2);
  }
  const x = Math.min(...xs) - PAD;
  const y = Math.min(...ys) - PAD;
  return {
    radius: RADIUS,
    lines,
    dots,
    texts,
    box: {
      x,
      y,
      width: Math.max(...xs) + PAD - x,
      height: Math.max(...ys) + PAD - y,
    },
  };
}
